import * as React from 'react';
import { useLocation } from 'react-router';
import { useLatestRef } from '@epihub/web/src/hooks/useLatestRef';
import { uuidv4 } from '@epihub/core/src/fn/uuidv4';
import { type ActionEvent, ACTIONS } from '@krishanmadan/core/src/model/actions';
import { log } from '../_ctx/log';
import { handleActionEvent } from './useActionListener';

export const useLinkInterceptor = () => {
  const location = useLocation();
  const locationRef = useLatestRef(location);

  const onInterceptLinkRef = useLatestRef((event: MouseEvent) => {
    const target = (event.target || event.srcElement) as HTMLElement | null;
    const anchor = target?.closest('a');

    if (!anchor) {
      return;
    }

    const href = anchor.getAttribute('href');
    if (!href) {
      return;
    }

    let linkUrl: URL;
    try {
      linkUrl = new URL(href, window.location.origin);
    } catch {
      return;
    }

    const currentPathname = locationRef.current.pathname;
    if (linkUrl.pathname !== currentPathname) {
      return;
    }

    log.v('useLinkInterceptor', `Intercepted same-page link: ${href}`);
    event.preventDefault();

    const clickId = uuidv4();
    const sp = new URLSearchParams(linkUrl.search);

    for (const [, action] of Object.entries(ACTIONS)) {
      const payload = action.onSearchParams(sp);
      if (payload != null) {
        handleActionEvent({
          actionId: `${clickId}-${action.type}`,
          type: action.type,
          payload,
        } as ActionEvent);
      }
    }
  });

  React.useEffect(() => {
    function listener(event: MouseEvent) {
      onInterceptLinkRef.current(event);
    }

    document.addEventListener('click', listener);

    return () => {
      document.removeEventListener('click', listener);
    };
  }, [onInterceptLinkRef]);
};
